import { useEffect, useState, useSyncExternalStore } from 'react';
import { api } from './client';

export type ValueType = 'bool' | 'number' | null;

export interface OptionItem {
  id: string;
  dict_key: string;
  value: string;
  label: string;
  hint: string | null;
  sort_order: number;
  value_type: ValueType;
}

export interface OptionItemIn {
  dict_key: string;
  value: string;
  label: string;
  hint?: string | null;
  sort_order?: number;
  value_type?: ValueType;
}

export interface OptionItemUpdate {
  value?: string;
  label?: string;
  hint?: string | null;
  sort_order?: number;
  value_type?: ValueType;
}

export const optionsApi = {
  list: (dict_key?: string) =>
    api.get<OptionItem[]>('/api/v1/options', { params: dict_key ? { dict_key } : {} }).then(r => r.data),
  keys: () => api.get<string[]>('/api/v1/options/keys').then(r => r.data),
  create: (p: OptionItemIn) => api.post<OptionItem>('/api/v1/options', p).then(r => r.data),
  update: (id: string, p: OptionItemUpdate) =>
    api.patch<OptionItem>(`/api/v1/options/${id}`, p).then(r => r.data),
  remove: (id: string) => api.delete(`/api/v1/options/${id}`),
  reorder: (dict_key: string, items: { id: string; sort_order: number }[]) =>
    api.post('/api/v1/options/reorder', { dict_key, items }),
};

// ── Module-level cache + subscribers ────────────────────────────────────────
// useOptions(key) выдаёт массив для конкретного dict_key. Кэш живёт пока
// открыта вкладка; invalidate(key) сбрасывает кеш и шлёт уведомление всем,
// кто подписан на этот key (так Settings обновляет форму инсталляции).
type Cache = Map<string, OptionItem[]>;
const cache: Cache = new Map();
const inflight: Map<string, Promise<OptionItem[]>> = new Map();
type Listener = () => void;
const listeners: Map<string, Set<Listener>> = new Map();

function notify(key: string) {
  listeners.get(key)?.forEach(l => { try { l(); } catch { /* ignore */ } });
}

function subscribe(key: string, l: Listener): () => void {
  let set = listeners.get(key);
  if (!set) { set = new Set(); listeners.set(key, set); }
  set.add(l);
  return () => { set!.delete(l); };
}

async function loadKey(key: string): Promise<OptionItem[]> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = optionsApi.list(key).then(items => {
    cache.set(key, items);
    inflight.delete(key);
    notify(key);
    return items;
  }).catch(err => {
    inflight.delete(key);
    throw err;
  });
  inflight.set(key, p);
  return p;
}

export function invalidateOptions(key?: string): void {
  if (key) {
    cache.delete(key);
    inflight.delete(key);
    void loadKey(key);  // подгрузим заново и уведомим подписчиков
  } else {
    for (const k of Array.from(cache.keys())) invalidateOptions(k);
  }
}

export function useOptions(key: string): { items: OptionItem[]; loading: boolean } {
  const snap = useSyncExternalStore(
    cb => subscribe(key, cb),
    () => cache.get(key),
    () => undefined,
  );
  const [loading, setLoading] = useState(() => !cache.has(key));
  useEffect(() => {
    if (cache.has(key)) { setLoading(false); return; }
    setLoading(true);
    loadKey(key).finally(() => setLoading(false));
  }, [key]);
  return { items: snap || [], loading };
}

// Удобный хелпер: label по value (для read-only сводок).
export function findLabel(items: OptionItem[], value: string | null | undefined): string {
  if (!value) return '';
  return items.find(i => i.value === value)?.label || value;
}
